import React from 'react';
import PropTypes from 'prop-types';

import {
  Button,
  Col,
  Form,
  Image,
  OverlayTrigger,
  Popover,
  Row,
  Spinner,
  Table,
  Tooltip,
} from 'react-bootstrap';
import { css, StyleSheet } from 'aphrodite';
import { faSearch } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  brandCleanupUserActionsOptions,
  suggestedActionsOptions,
} from 'lib/enums/BrandCleanupEnums';
import Container from 'components/shared/Container';
import { createGoogleSearchUrl } from 'lib/utils/webSearchUtils';
import { greyBackground } from 'lib/css/colors';
import LoadingSpinner from 'components/shared/LoadingSpinner';
import PaginationComponent from 'components/shared/PaginationComponent';
import placeholderProductImage from 'images/placeholder-img-product-v2.svg';
import pointerOnHover from 'lib/css/pointerOnHover';
import PrivateLabelBrandBadge from 'components/manage_brands/brand_details/PrivateLabelBrandBadge';
import VerificationStatusIcon from 'components/global_products/detail_view/VerificationStatusIcon';

function BrandCleanupProductsTable({
  areAllOnPageSelected,
  handleSelectAllOnPage,
  areSomeOnPageSelected,
  products,
  productCount,
  currentPage,
  setCurrentPage,
  itemsPerPage,
  setItemsPerPage,
  loadingProducts,
  loadingProductCount,
  cleaningProducts,
  handleRowClick,
  selectedProductIds,
  setSelectedProductIds,
  hasCleanupBrandsPermission,
  traineeWorkOnly,
  refetchProducts,
  refetchProductCount,
  refetchBrands,
  refetchSuggestedBrands,
  refetchDistributorBrandNames,
  refetchL2Categories,
  refetchL3Categories,
  refetchL4Categories,
  GMVProductsOnly,
}) {
  return (
    <Container>
      <Row className="d-flex mb-2 justify-content-end">
        <Button
          className="mr-2"
          variant="outline-primary"
          onClick={() => {
            refetchProducts();
            refetchProductCount();
            refetchBrands();
            refetchSuggestedBrands();
            refetchDistributorBrandNames();
            refetchL2Categories();
            refetchL3Categories();
            refetchL4Categories();
          }}
        >
          Refresh
        </Button>
        {loadingProductCount ? (
          <Spinner animation="border" role="status" size="sm" />
        ) : (
          <span className="mr-2">{productCount} products found</span>
        )}
        <PaginationComponent
          totalItems={productCount}
          itemsPerPage={itemsPerPage}
          setItemsPerPage={setItemsPerPage}
          currentPage={currentPage}
          setCurrentPage={setCurrentPage}
        />
      </Row>
      <Row>
        <Col md={12}>
          <Table striped bordered hover>
            <thead>
              <tr>
                <th>
                  <Form.Check
                    type="checkbox"
                    checked={areAllOnPageSelected()}
                    onChange={handleSelectAllOnPage}
                    ref={input => {
                      if (input) {
                        input.indeterminate = areSomeOnPageSelected();
                      }
                    }}
                    disabled={!products || products.length === 0}
                  />
                </th>
                <th>Product Image</th>
                <th>Product Name</th>
                <th>Current Brand and Manufacturer Name</th>
                <th>Distributor Brand Name</th>
                <th>Distributor Manufacturer Name</th>
                <th>AI Suggested Brand and Manufacturer Name</th>
                {hasCleanupBrandsPermission && traineeWorkOnly && (
                  <th>Trainee Suggested Brand and Manufacturer Name</th>
                )}
                <th>L4 Category</th>
                <th>AI Suggested Action</th>
                {GMVProductsOnly && <th>GMV</th>}
                <th>View</th>
              </tr>
            </thead>
            {loadingProducts || cleaningProducts ? (
              <tbody>
                <tr>
                  <td
                    colSpan={
                      10 +
                      (hasCleanupBrandsPermission && traineeWorkOnly ? 1 : 0) +
                      (GMVProductsOnly ? 1 : 0)
                    }
                  >
                    <LoadingSpinner />
                  </td>
                </tr>
              </tbody>
            ) : (
              <tbody>
                {products?.map(product => (
                  <tr
                    key={product.id}
                    onClick={() => handleRowClick(product.id)}
                    className={css(styles.clickableRow)}
                  >
                    <td onClick={e => e.stopPropagation()}>
                      <Form.Check
                        type="checkbox"
                        checked={selectedProductIds.includes(product.id)}
                        onChange={e => {
                          if (e.target.checked) {
                            setSelectedProductIds([...selectedProductIds, product.id]);
                          } else {
                            setSelectedProductIds(
                              selectedProductIds.filter(id => id !== product.id),
                            );
                          }
                        }}
                      />
                    </td>
                    <td>
                      <OverlayTrigger
                        placement="right"
                        overlay={
                          <Popover
                            id={`popover-product-image-${product.id}`}
                            style={{ maxWidth: 400 }}
                          >
                            <Popover.Content>
                              <Image
                                width={400}
                                fluid
                                src={
                                  product.global_product_vendor?.thumbnail_image?.url ||
                                  placeholderProductImage
                                }
                              />
                            </Popover.Content>
                          </Popover>
                        }
                      >
                        <Image
                          src={
                            product.global_product_vendor?.thumbnail_image?.url ||
                            placeholderProductImage
                          }
                          width={50}
                          height={50}
                          thumbnail
                        />
                      </OverlayTrigger>
                    </td>
                    <td>{product.global_product_vendor._name || '-'}</td>
                    <td>
                      <div>
                        <Button
                          variant="link"
                          onClick={() => {
                            window.open(
                              // eslint-disable-next-line
                              `/enrich-products/brand-details/${product.global_product_vendor.brand?.id}`,
                              '_blank',
                            );
                          }}
                        >
                          {product.global_product_vendor.brand?.name || '-'}
                        </Button>
                        <VerificationStatusIcon
                          verificationStatus={
                            product.global_product_vendor.brand?.verification_status
                          }
                        />
                        <PrivateLabelBrandBadge brand={product.global_product_vendor.brand} />
                        {product.global_product_vendor.brand?.global_manufacturer && ' by'}
                      </div>
                      {product.global_product_vendor.brand?.global_manufacturer && (
                        <div>
                          <Button
                            variant="link"
                            onClick={() => {
                              window.open(
                                // eslint-disable-next-line
                                `/enrich-products/manufacturer-details/${product.global_product_vendor.brand?.global_manufacturer?.id}`,
                                '_blank',
                              );
                            }}
                          >
                            {product.global_product_vendor.brand?.global_manufacturer?.name || '-'}
                          </Button>
                          <VerificationStatusIcon
                            verificationStatus={
                              product.global_product_vendor.brand?.global_manufacturer
                                ?.verification_status
                            }
                          />
                        </div>
                      )}
                    </td>
                    <td>{product.distributor_brand_name || '-'}</td>
                    <td>{product.distributor_manufacturer_name || '-'}</td>
                    <td>
                      <div>
                        <Button
                          variant="link"
                          onClick={() => {
                            window.open(
                              // eslint-disable-next-line
                              `/enrich-products/brand-details/${product.suggested_brand?.id}`,
                              '_blank',
                            );
                          }}
                        >
                          {product.suggested_brand?.name || '-'}
                        </Button>
                        <VerificationStatusIcon
                          verificationStatus={product.suggested_brand?.verification_status}
                        />
                        <PrivateLabelBrandBadge brand={product.suggested_brand} />
                        {product.suggested_brand?.global_manufacturer && ' by'}
                      </div>
                      {product.suggested_brand?.global_manufacturer && (
                        <div>
                          <Button
                            variant="link"
                            onClick={() => {
                              window.open(
                                // eslint-disable-next-line
                                `/enrich-products/manufacturer-details/${product.suggested_brand?.global_manufacturer?.id}`,
                                '_blank',
                              );
                            }}
                          >
                            {product.suggested_brand?.global_manufacturer?.name || '-'}
                          </Button>
                          <VerificationStatusIcon
                            verificationStatus={
                              product.suggested_brand?.global_manufacturer?.verification_status
                            }
                          />
                        </div>
                      )}
                    </td>
                    {hasCleanupBrandsPermission && traineeWorkOnly && (
                      <td>
                        <strong className="ml-2">
                          {brandCleanupUserActionsOptions.find(
                            action => action.value === product?.trainee_user_action,
                          )?.label || '-'}
                        </strong>
                        {product.trainee_suggested_brand && (
                          <div>
                            <Button
                              variant="link"
                              onClick={() => {
                                window.open(
                                  // eslint-disable-next-line
                                  `/enrich-products/brand-details/${product.trainee_suggested_brand?.id}`,
                                  '_blank',
                                );
                              }}
                            >
                              {product.trainee_suggested_brand?.name || '-'}
                            </Button>
                            <VerificationStatusIcon
                              verificationStatus={
                                product.trainee_suggested_brand?.verification_status
                              }
                            />
                            <PrivateLabelBrandBadge brand={product.trainee_suggested_brand} />
                            {product.trainee_suggested_brand?.global_manufacturer && ' by'}
                          </div>
                        )}
                        {product.trainee_suggested_brand?.global_manufacturer && (
                          <div>
                            <Button
                              variant="link"
                              onClick={() => {
                                window.open(
                                  // eslint-disable-next-line
                                  `/enrich-products/manufacturer-details/${product.trainee_suggested_brand?.global_manufacturer?.id}`,
                                  '_blank',
                                );
                              }}
                            >
                              {product.trainee_suggested_brand?.global_manufacturer?.name || '-'}
                            </Button>
                            <VerificationStatusIcon
                              verificationStatus={
                                product.trainee_suggested_brand?.global_manufacturer
                                  ?.verification_status
                              }
                            />
                          </div>
                        )}
                      </td>
                    )}
                    <td>
                      {product.global_product_vendor.global_product?.l4_category?.category_name ||
                        '-'}
                    </td>
                    <td>
                      {suggestedActionsOptions.find(
                        action => action.value === product?.suggested_action,
                      )?.label || '-'}
                      {product?.confidence_score != null && ` (${product.confidence_score})`}
                    </td>
                    {GMVProductsOnly && <td>{product.product_gmv || '-'}</td>}
                    <td onClick={e => e.stopPropagation()}>
                      <Button
                        variant="link"
                        onClick={() => {
                          window.open(
                            // eslint-disable-next-line
                            `/global-product-detail/${product.global_product_vendor.global_product_id}/${product.global_product_vendor.id}/distributor`,
                            '_blank',
                          );
                        }}
                      >
                        View
                      </Button>
                      <OverlayTrigger overlay={<Tooltip>Search Product on Google</Tooltip>}>
                        <FontAwesomeIcon
                          icon={faSearch}
                          onClick={() =>
                            window.open(
                              createGoogleSearchUrl(
                                [
                                  product.global_product_vendor?.name,
                                  product.global_product_vendor?.brand?.name,
                                ],
                                false,
                              ),
                              '_blank',
                            )
                          }
                          className={pointerOnHover}
                        />
                      </OverlayTrigger>
                    </td>
                  </tr>
                ))}
              </tbody>
            )}
          </Table>
        </Col>
      </Row>
    </Container>
  );
}

const styles = StyleSheet.create({
  clickableRow: {
    cursor: 'pointer',
    ':hover': {
      backgroundColor: greyBackground,
    },
  },
});

BrandCleanupProductsTable.propTypes = {
  areAllOnPageSelected: PropTypes.func.isRequired,
  handleSelectAllOnPage: PropTypes.func.isRequired,
  areSomeOnPageSelected: PropTypes.func.isRequired,
  products: PropTypes.array.isRequired,
  productCount: PropTypes.number.isRequired,
  currentPage: PropTypes.number.isRequired,
  setCurrentPage: PropTypes.func.isRequired,
  itemsPerPage: PropTypes.number.isRequired,
  setItemsPerPage: PropTypes.func.isRequired,
  loadingProducts: PropTypes.bool.isRequired,
  loadingProductCount: PropTypes.bool.isRequired,
  cleaningProducts: PropTypes.bool.isRequired,
  handleRowClick: PropTypes.func.isRequired,
  selectedProductIds: PropTypes.array.isRequired,
  setSelectedProductIds: PropTypes.func.isRequired,
  hasCleanupBrandsPermission: PropTypes.bool.isRequired,
  traineeWorkOnly: PropTypes.bool.isRequired,
  refetchProducts: PropTypes.func.isRequired,
  refetchProductCount: PropTypes.func.isRequired,
  refetchBrands: PropTypes.func.isRequired,
  refetchSuggestedBrands: PropTypes.func.isRequired,
  refetchDistributorBrandNames: PropTypes.func.isRequired,
  refetchL2Categories: PropTypes.func.isRequired,
  refetchL3Categories: PropTypes.func.isRequired,
  refetchL4Categories: PropTypes.func.isRequired,
  GMVProductsOnly: PropTypes.bool.isRequired,
};

export default BrandCleanupProductsTable;
